/**
 * DataExportSection Component
 * 
 * Handles data export actions: save, restart, and download info file.
 * Displays action buttons based on current data state.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import SaveButton from '@/shared/components/buttons/SaveButton';
import RestartButton from '@/shared/components/buttons/RestartButton';

export interface DataExportSectionProps {
  /** Callback to trigger save modal */
  onSave: () => void;
  /** Callback to restart the workflow */
  onRestart: () => void;
  /** Whether there is any data loaded (files or raw data) */
  hasData: boolean;
  /** Whether there is interpolated data available */
  hasInterpolatedData: boolean;
}

export const DataExportSection: React.FC<DataExportSectionProps> = ({
  onSave,
  onRestart,
  hasData,
  hasInterpolatedData,
}) => {
  const { t } = useTranslation('dataCloud');

  // Don't render if no data at all
  if (!hasData && !hasInterpolatedData) {
    return null;
  }

  return (
    <div
      className="mt-8 flex justify-between"
      role="group"
      aria-label={t('common.actions', { ns: 'common' })}
    >
      {/* Restart Button - Show when there's any data */}
      {hasData && (
        <RestartButton
          onRestart={onRestart}
          aria-label={t('common.restart', { ns: 'common' })}
        >
          {t('common.restart', { ns: 'common' })}
        </RestartButton>
      )}

      {/* Save Button - Show only when interpolated data is available */}
      {hasInterpolatedData && (
        <SaveButton
          onSave={onSave}
          aria-label={t('fileNameModal.buttons.save', { ns: 'modals' })}
        >
          {t('fileNameModal.buttons.save', { ns: 'modals' })}
        </SaveButton>
      )}
    </div>
  );
};

export default DataExportSection;
